import {
  QueriesResults,
  QueryFunction,
  useMutation,
  UseMutationResult,
  useQueries,
  useQuery,
  UseQueryResult,
} from 'react-query'
import { AxiosError } from 'axios'
import { find, has } from 'lodash'

import { FlowState } from 'common/interfaces'
import api from 'utils/api'
import { getCompanySlug } from 'utils/flow'
import { useIsOnline, useLocalStorage } from 'utils/hook'
import {
  AdjusterInfo,
  AirtableClaimRequest,
  AirtableClaimResponse,
  AirtableClaims,
  AllAdjusters,
  HubSettings,
  SelectedClaim,
} from 'views/AdjustersHub/interfces'
import { useCurrentClaimId, useSetHubSettingsToLS } from 'views/AdjustersHub/utils/hooks'
import { OfflineRequestsService } from 'views/AdjustersHub/utils/offlineRequestsService'

const getOneAdjuster: QueryFunction<AdjusterInfo> = async () => {
  const response = await api.get<AdjusterInfo>('adjusters/me/')

  return response.data
}

const useOneAdjuster = (): UseQueryResult<AdjusterInfo, AxiosError> => {
  const isOnline = useIsOnline()

  const companySlug = getCompanySlug()
  const [ls, setLS] = useLocalStorage(`${companySlug}/adjusterInfo`, {})

  const query = useQuery<AdjusterInfo, AxiosError>(['oneAdjuster'], getOneAdjuster, {
    ...(!isOnline && { initialData: ls as unknown as AdjusterInfo }),
    onSuccess: (data) => {
      setLS(data)
    },
  })

  return query
}

const getAdjusters: QueryFunction<AllAdjusters> = async () => {
  const response = await api.get<AllAdjusters>('adjusters/')

  return response.data
}

const useAdjusters = (): UseQueryResult<AllAdjusters, AxiosError> => {
  const query = useQuery<AllAdjusters, AxiosError>(['adjusters'], getAdjusters)

  return query
}

const getAdjustersHubSettings: QueryFunction<HubSettings> = async () => {
  const response = await api.get<HubSettings>('adjusters/hub_settings/')

  return response.data
}

const useAdjustersHubSettings = (): UseQueryResult<HubSettings, AxiosError> => {
  const isOnline = useIsOnline()

  const companySlug = getCompanySlug()
  const [ls] = useLocalStorage(`${companySlug}/hubSettings`, {})

  const setHubSettingsToLS = useSetHubSettingsToLS()

  const query = useQuery<HubSettings, AxiosError>(
    ['adjustersHubSettings'],
    getAdjustersHubSettings,
    {
      ...(!isOnline && { initialData: ls as unknown as HubSettings }),
      onSuccess: setHubSettingsToLS,
    }
  )

  return query
}

// AIRTABLE CLAIMS START //

const getAirtableClaims: QueryFunction<AirtableClaims> = async () => {
  const response = await api.get<AirtableClaims>('airtable_claims/')

  return response.data
}

const useAirtableClaims = (): UseQueryResult<AirtableClaims, AxiosError> => {
  const isOnline = useIsOnline()

  const currentClaimId = useCurrentClaimId()

  const companySlug = getCompanySlug()
  const [ls, setLS] = useLocalStorage<AirtableClaimResponse[]>(`${companySlug}/airtableClaims`, [])
  const [, setSelectedClaim] = useLocalStorage<SelectedClaim>(
    'selectedClaim',
    {} as unknown as SelectedClaim
  )

  const { isSuccess: hubDataLoaded, data: hubData } = useAdjustersHubSettings()
  const dateOfLoss = hubData?.dictionary['Date of loss']

  const { isSuccess: adjusterFlowLoaded, data: adjusterFlowObj } = useAdjusterFlow()

  const query = useQuery<AirtableClaims, AxiosError>(['airtableClaims'], getAirtableClaims, {
    ...(!isOnline && {
      initialData: {
        next: '',
        results: ls as unknown as AirtableClaims['results'],
      },
    }),
    enabled: hubDataLoaded && adjusterFlowLoaded,
    select: (data) => {
      const results = data.results.map((el) => {
        if (el.fields[dateOfLoss!] !== undefined) {
          el.fields[dateOfLoss!] = new Date(el.fields[dateOfLoss!]).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        }

        return el
      })

      return { ...data, results }
    },
    onSuccess: ({ results }) => {
      setLS(results)

      // If you open the one claim page first
      if (currentClaimId !== '') {
        const currentClaim = find(results, { id: currentClaimId })!
        const currentClaimFlowName = currentClaim?.fields[hubData!.flow]
        const flowKey = has(adjusterFlowObj, currentClaimFlowName)
          ? currentClaimFlowName
          : (hubData?.default_flow as string)

        setSelectedClaim({
          ...currentClaim,
          flowKey,
        })
      }
    },
  })

  return query
}

const updateAirtableClaim = async (
  requestVars: AirtableClaimRequest
): Promise<AirtableClaimResponse> => {
  const { id: claimID, claimData } = requestVars

  const response = await api.patch<AirtableClaimResponse>(`airtable_claims/${claimID}/`, claimData)

  return response.data
}

const useUpdateAirtableClaim = (): UseMutationResult<
  AirtableClaimResponse,
  unknown,
  AirtableClaimRequest
> => {
  const isOnline = useIsOnline()

  return useMutation<AirtableClaimResponse, unknown, AirtableClaimRequest>(updateAirtableClaim, {
    onError: async (error, params) => {
      if (!isOnline) {
        await OfflineRequestsService.saveRequest({
          method: 'useUpdateAirtableClaim',
          payload: params,
          claimID: params.id,
        })

        return
      }

      console.log('error useUpdateAirtableClaim', error)
    },
  })
}

// AIRTABLE CLAIMS END //

type AdjustersFlows = Record<string, FlowState>

const getAdjusterFlow: QueryFunction<AdjustersFlows> = async () => {
  const response = await api.get<AdjustersFlows>('flows/for_adjusters/')

  return response.data
}

const useAdjusterFlow = (): UseQueryResult<AdjustersFlows, AxiosError> => {
  const isOnline = useIsOnline()

  const companySlug = getCompanySlug()
  const [ls, setLS] = useLocalStorage(`${companySlug}/adjusterFlow`, {})

  const query = useQuery<AdjustersFlows, AxiosError>(['adjusterFlow'], getAdjusterFlow, {
    ...(!isOnline && { initialData: ls as unknown as AdjustersFlows }),
    onSuccess: (data) => {
      setLS(data)
    },
  })

  return query
}

const useAdjusterQueries = (): QueriesResults<any[]> => {
  const isOnline = useIsOnline()

  const companySlug = getCompanySlug()
  const [lsAI, setLSAI] = useLocalStorage(`${companySlug}/adjusterInfo`, {})
  const [lsHS] = useLocalStorage(`${companySlug}/hubSettings`, {})
  const [lsAF, setLSAF] = useLocalStorage(`${companySlug}/adjusterFlow`, {})

  const setHubSettingsToLS = useSetHubSettingsToLS()

  return useQueries([
    {
      queryKey: 'oneAdjuster',
      queryFn: getOneAdjuster,
      ...(!isOnline && { initialData: lsAI as unknown as AdjusterInfo }),
      onSuccess: (data: AdjusterInfo) => {
        setLSAI(data)
      },
    },
    {
      queryKey: 'adjustersHubSettings',
      queryFn: getAdjustersHubSettings,
      ...(!isOnline && { initialData: lsHS as unknown as HubSettings }),
      onSuccess: setHubSettingsToLS,
    },
    {
      queryKey: 'adjusterFlow',
      queryFn: getAdjusterFlow,
      ...(!isOnline && { initialData: lsAF as unknown as AdjustersFlows }),
      onSuccess: (data: AdjustersFlows) => {
        setLSAF(data)
      },
    },
  ])
}

export {
  useOneAdjuster,
  useAdjusters,
  useAdjustersHubSettings,
  useAirtableClaims,
  useUpdateAirtableClaim,
  useAdjusterFlow,
  useAdjusterQueries,
}
